import { forwardRef, HTMLAttributes } from "react";
import { classNames } from "../utils/classNames";
import { fontWeightMapping, textSizeMapping } from "./tailwindMappings";
import { BoxProps } from "./boxHelpers";

export type AvatarProps = {
  color?: "neutral" | "secondary" | "primary";
  className?: string;
  textSize?: BoxProps["textSize"];
  fontWeight?: BoxProps["fontWeight"];
  size?: "sm" | "md" | "lg";
};

function prepareProps<T extends AvatarProps>({
  className,
  color,
  textSize = "lg",
  fontWeight = "bold",
  size = "sm",
  ...props
}: T) {
  return {
    className: classNames(
      className,
      "tw-flex-none",
      "tw-overflow-hidden",
      "tw-rounded-full",
      size === "sm" && ["tw-w-[3rem]", "tw-h-[3rem]"],
      size === "md" && ["tw-w-24", "tw-h-24"],
      size === "lg" && ["tw-w-40", "tw-h-40"],
      "tw-flex",
      "tw-items-center",
      "tw-justify-center",
      textSizeMapping[textSize],
      color === "secondary"
        ? "bg-secondary-light color-default"
        : color === "primary"
          ? "bg-primary-light color-primary-dark"
          : "bg-gray-hard-dimmed75 color-gray-xx-soft",
      fontWeightMapping[fontWeight],
    ),
    ...props,
  };
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps & HTMLAttributes<HTMLDivElement>>(
  function Avatar({ children, ...props }, ref) {
    return (
      <div ref={ref} {...prepareProps(props)}>
        {children}
      </div>
    );
  },
);

export const AvatarButton = forwardRef<
  HTMLButtonElement,
  AvatarProps & HTMLAttributes<HTMLButtonElement>
>(function AvatarButton({ children, ...props }, ref) {
  return (
    <button ref={ref} {...prepareProps(props)}>
      {children}
    </button>
  );
});
